/**
 * Generic array manipulation utilities for list reordering and toggling
 */

/**
 * Move an item up or down in an array by its id property
 * @param items - Array of items with id property
 * @param itemId - ID of item to move
 * @param direction - 'up' (move to lower index) or 'down' (move to higher index)
 * @returns New array with item moved, or original array if operation not possible
 */
export function moveItemInArray<T extends { id: string }>(
  items: T[],
  itemId: string,
  direction: "up" | "down"
): T[] {
  const idx = items.findIndex((item) => item.id === itemId);

  // Bounds check
  if (direction === "up" && idx <= 0) return items;
  if (direction === "down" && idx >= items.length - 1) return items;
  if (idx === -1) return items; // Item not found

  // Create new array with items swapped
  const newItems = [...items];
  const targetIdx = direction === "up" ? idx - 1 : idx + 1;
  [newItems[idx], newItems[targetIdx]] = [newItems[targetIdx], newItems[idx]];

  return newItems;
}

/**
 * Toggle the hidden/visibility property of an item in an array
 * @param items - Array of items with id and hidden properties
 * @param itemId - ID of item to toggle
 * @returns New array with item's hidden property toggled
 */
export function toggleItemVisibility<T extends { id: string; hidden?: boolean }>(
  items: T[],
  itemId: string
): T[] {
  return items.map((item) =>
    item.id === itemId ? { ...item, hidden: !item.hidden } : item
  );
}
